import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type {
  TaxDocumentListItem,
  ListTaxDocumentsResponse,
} from "../types";

export interface TaxDocumentsFilters {
  status?: string;
  documentType?: string;
  fromDate?: string;
  toDate?: string;
}

export interface UseTaxDocumentsOptions {
  filters?: TaxDocumentsFilters;
  limit?: number;
  offset?: number;
}

export interface UseTaxDocumentsResult {
  documents: TaxDocumentListItem[];
  total: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useTaxDocuments(
  options: UseTaxDocumentsOptions = {}
): UseTaxDocumentsResult {
  const { filters = {}, limit = 50, offset = 0 } = options;

  const [documents, setDocuments] = useState<TaxDocumentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.documentType) params.append("documentType", filters.documentType);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      params.append("limit", String(limit));
      params.append("offset", String(offset));

      const queryString = params.toString();
      const url = `/api/tax-documents${queryString ? `?${queryString}` : ""}`;

      const response = await apiFetch.get<ListTaxDocumentsResponse>(url);
      setDocuments(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch documents"));
    } finally {
      setIsLoading(false);
    }
  }, [filters.status, filters.documentType, filters.fromDate, filters.toDate, limit, offset]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    total,
    isLoading,
    error,
    refetch: fetchDocuments,
  };
}
